//src/pages/gro/SurveyRiskSuggestionsPage.tsx
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { SurveyRiskSuggestionModerationTable } from "@/components/gro/SurveyRiskSuggestionModerationTable";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import {
  useApproveAndConvertSurveyRiskSuggestion,
  useApproveSurveyRiskSuggestion,
  useConvertSurveyRiskSuggestion,
  useRejectSurveyRiskSuggestion,
  useResetSurveyRiskSuggestion,
  useSurveyRiskSuggestionDetail,
  useSurveyRiskSuggestionLogs,
  useSurveyRiskSuggestionsQueue,
  useSurveyRiskSuggestionsSummary,
} from "@/hooks/useSurveyRiskSuggestions";
import { useRiskInventories } from "@/hooks/useRiskInventories";
import { useGroSettings } from "@/hooks/useGroSettings";
import { useSurveyCampaigns } from "@/hooks/useSurveyCampaigns";
import type { SurveyRiskSuggestionQueueItem, SurveyRiskSuggestionStatus, SurveyResultCutType } from "@/types/surveyRiskSuggestions";
import { PaginationFooter } from "@/components/shared/PaginationFooter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type ActionMode = "approve" | "reject" | "reset" | "convert" | "approve_and_convert" | null;

function statusLabel(status?: string | null) {
  if (status === "approved") return "Aprovado";
  if (status === "auto_approved") return "Autoaprovado";
  if (status === "rejected") return "Rejeitado";
  if (status === "converted_to_inventory") return "No inventário";
  return "Pendente";
}
function cutTypeLabel(cutType?: string | null) {
  if (cutType === "unit") return "Unidade";
  if (cutType === "department") return "Departamento";
  if (cutType === "job_position") return "Cargo";
  return "Grupo de Avaliação";
}
function formatDate(value?: string | null) {
  if (!value) return "-";
  return new Date(value).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}
function actionLabel(action: string) {
  if (action === "create") return "Criou";
  if (action === "approve") return "Aprovou";
  if (action === "reject") return "Rejeitou";
  if (action === "reset_to_pending") return "Voltou para pendente";
  if (action === "convert_to_inventory") return "Converteu para inventário";
  if (action === "approve_and_convert") return "Aprovou e converteu";
  return action;
}
function infoAfterDecision(status?: string, value?: string | null) {
  if (status !== "approved" && status !== "rejected") return "Risco pendente de aprovação ou rejeição.";
  return value?.trim() ? value : "Não informado.";
}

export default function SurveyRiskSuggestionsPage() {
  const { user } = useAuth();
  const canRead = user?.permissions?.includes(PERMISSIONS.SURVEY_RESULTS_READ) ?? false;
  const canWrite = user?.permissions?.includes(PERMISSIONS.RISK_INVENTORY_WRITE) ?? false;

  const [statusFilter, setStatusFilter] = useState<SurveyRiskSuggestionStatus | "all">("pending");
  const [cutTypeFilter, setCutTypeFilter] = useState<SurveyResultCutType | "all">("all");
  const [riskLevelFilter, setRiskLevelFilter] = useState<"all" | "low" | "moderate" | "high" | "critical">("all");
  const [campaignFilter, setCampaignFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<10 | 20 | 30>(10);

  const [selectedItem, setSelectedItem] = useState<SurveyRiskSuggestionQueueItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [logsItem, setLogsItem] = useState<SurveyRiskSuggestionQueueItem | null>(null);
  const [logsOpen, setLogsOpen] = useState(false);

  const [actionMode, setActionMode] = useState<ActionMode>(null);
  const [actionItem, setActionItem] = useState<SurveyRiskSuggestionQueueItem | null>(null);
  const [justification, setJustification] = useState("");
  const [technicalNotes, setTechnicalNotes] = useState("");
  const [inventoryId, setInventoryId] = useState("");

  const queueQ = useSurveyRiskSuggestionsQueue({
    campaign_id: campaignFilter === "all" ? undefined : campaignFilter,
    status: statusFilter === "all" ? undefined : statusFilter,
    cut_type: cutTypeFilter === "all" ? undefined : cutTypeFilter,
    risk_level_code: riskLevelFilter === "all" ? undefined : riskLevelFilter,
    page,
    per_page: perPage,
  });
  const summaryQ = useSurveyRiskSuggestionsSummary({
    campaign_id: campaignFilter === "all" ? undefined : campaignFilter,
    status: statusFilter === "all" ? undefined : statusFilter,
    cut_type: cutTypeFilter === "all" ? undefined : cutTypeFilter,
    risk_level_code: riskLevelFilter === "all" ? undefined : riskLevelFilter,
  });
  const inventoriesQ = useRiskInventories({ status: "active", unit_id: "" });
  const campaignsQ = useSurveyCampaigns({ status: "closed" });
  const groSettingsQ = useGroSettings();
  const detailQ = useSurveyRiskSuggestionDetail(selectedItem?.id);
  const logsQ = useSurveyRiskSuggestionLogs(logsItem?.id);

  const approveMut = useApproveSurveyRiskSuggestion();
  const rejectMut = useRejectSurveyRiskSuggestion();
  const resetMut = useResetSurveyRiskSuggestion();
  const convertMut = useConvertSurveyRiskSuggestion();
  const approveAndConvertMut = useApproveAndConvertSurveyRiskSuggestion();

  const pageData = queueQ.data ?? { items: [], total: 0, page: 1, per_page: perPage, total_pages: 1 };
  const items = pageData.items;
  const inventories = inventoriesQ.data ?? [];
  const closedCampaigns = campaignsQ.data ?? [];
  const summary = summaryQ.data ?? { total: 0, pending: 0, approved: 0, rejected: 0, converted_to_inventory: 0 };
  const moderationEnabled = groSettingsQ.data?.risk_suggestion_mode !== "auto_approve";

  const mutationBusy = approveMut.isPending || rejectMut.isPending || resetMut.isPending || convertMut.isPending || approveAndConvertMut.isPending;

  function openDetail(item: SurveyRiskSuggestionQueueItem) {
    setSelectedItem(item);
    setDetailOpen(true);
  }
  function openLogs(item: SurveyRiskSuggestionQueueItem) {
    setLogsItem(item);
    setLogsOpen(true);
  }
  function closeActionDialog() {
    if (mutationBusy) return;
    setActionMode(null);
    setActionItem(null);
    setJustification("");
    setTechnicalNotes("");
    setInventoryId("");
  }
  function openAction(mode: ActionMode, item: SurveyRiskSuggestionQueueItem) {
    setActionMode(mode);
    setActionItem(item);
    setJustification(item.justification_text || "");
    setTechnicalNotes(item.technical_notes_text || "");
    setInventoryId(item.inventory_id || "");
  }

  async function confirmAction() {
    if (!actionItem || !actionMode) return;
    try {
      if (actionMode === "approve") {
        await approveMut.mutateAsync({ suggestionId: actionItem.id, payload: { justification: justification || undefined, technical_notes: technicalNotes || undefined, convert_to_inventory: false } });
        toast.success("Sugestão aprovada com sucesso.");
      }
      if (actionMode === "reject") {
        if (justification.trim().length < 3) return toast.error("Informe o motivo da rejeição.");
        await rejectMut.mutateAsync({ suggestionId: actionItem.id, payload: { justification: justification.trim(), technical_notes: technicalNotes || undefined } });
        toast.success("Sugestão rejeitada com sucesso.");
      }
      if (actionMode === "reset") {
        await resetMut.mutateAsync({ suggestionId: actionItem.id, payload: { justification: justification || undefined, technical_notes: technicalNotes || undefined } });
        toast.success("Sugestão retornada para pendente.");
      }
      if ((actionMode === "convert" || actionMode === "approve_and_convert") && !inventoryId.trim()) {
        return toast.error("Selecione um inventário ativo para conversão.");
      }
      if (actionMode === "convert") {
        await convertMut.mutateAsync({ suggestionId: actionItem.id, payload: { justification: justification || undefined, technical_notes: technicalNotes || undefined, inventory_id: inventoryId || undefined } });
        toast.success("Sugestão convertida em item do inventário.");
      }
      if (actionMode === "approve_and_convert") {
        await approveAndConvertMut.mutateAsync({ suggestionId: actionItem.id, payload: { justification: justification || undefined, technical_notes: technicalNotes || undefined, inventory_id: inventoryId || undefined } });
        toast.success("Sugestão aprovada e convertida em item do inventário.");
      }
      closeActionDialog();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao executar ação.");
    }
  }

  return (
    <div>
      <PageHeader title="Fila de moderação de riscos" description="Centralize a decisão, a análise técnica e a conversão para inventário." />

      {!canRead ? (
        <Card className="p-6 text-sm text-destructive">Você não possui permissão para visualizar a fila de moderação.</Card>
      ) : groSettingsQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">Carregando configuração do GRO...</Card>
      ) : !moderationEnabled ? (
        <Card className="p-6 text-sm text-muted-foreground">
          A fila de moderação está desabilitada porque o modo de sugestão de risco está configurado como aprovação automática.
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Total</div><div className="mt-2 text-2xl font-semibold">{summary.total}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Pendentes</div><div className="mt-2 text-2xl font-semibold">{summary.pending}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Aprovadas</div><div className="mt-2 text-2xl font-semibold">{summary.approved}</div><div className="mt-1 text-xs text-muted-foreground">Inclui autoaprovadas.</div></Card>
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Rejeitadas</div><div className="mt-2 text-2xl font-semibold">{summary.rejected}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">No inventário</div><div className="mt-2 text-2xl font-semibold">{summary.converted_to_inventory}</div></Card>
          </div>

          <Card className="p-4">
            <div className="grid gap-3 lg:grid-cols-4">
              <div>
                <div className="mb-2 text-sm font-medium">Status</div>
                <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value as SurveyRiskSuggestionStatus | "all"); setPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pending">Pendentes</SelectItem>
                    <SelectItem value="approved">Aprovadas</SelectItem>
                    <SelectItem value="rejected">Rejeitadas</SelectItem>
                    <SelectItem value="converted_to_inventory">No inventário</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="mb-2 text-sm font-medium">Tipo de corte</div>
                <Select value={cutTypeFilter} onValueChange={(value) => { setCutTypeFilter(value as SurveyResultCutType | "all"); setPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="unit">Unidade</SelectItem>
                    <SelectItem value="department">Departamento</SelectItem>
                    <SelectItem value="job_position">Cargo</SelectItem>
                    <SelectItem value="worker_group">Grupo de Avaliação</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="mb-2 text-sm font-medium">Classificação</div>
                <Select value={riskLevelFilter} onValueChange={(value) => { setRiskLevelFilter(value as any); setPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="low">Baixo</SelectItem>
                    <SelectItem value="moderate">Moderado</SelectItem>
                    <SelectItem value="high">Alto</SelectItem>
                    <SelectItem value="critical">Crítico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="mb-2 text-sm font-medium">Campanha encerrada</div>
                <Select value={campaignFilter} onValueChange={(value) => { setCampaignFilter(value); setPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas encerradas</SelectItem>
                    {closedCampaigns.map((campaign) => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {queueQ.isLoading || summaryQ.isLoading || campaignsQ.isLoading ? (
            <Card className="p-6 text-sm text-muted-foreground">Carregando fila de moderação...</Card>
          ) : queueQ.isError || summaryQ.isError ? (
            <Card className="p-6 text-sm text-destructive">{(queueQ.error as any)?.message || (summaryQ.error as any)?.message || "Erro ao carregar fila de moderação."}</Card>
          ) : (
            <>
              <SurveyRiskSuggestionModerationTable
                items={items}
                canWrite={canWrite}
                busy={mutationBusy}
                onView={openDetail}
                onLogs={openLogs}
                onApprove={(item) => openAction("approve", item)}
                onReject={(item) => openAction("reject", item)}
                onReset={(item) => openAction("reset", item)}
                onConvert={(item) => openAction("convert", item)}
                onApproveAndConvert={(item) => openAction("approve_and_convert", item)}
              />
              <PaginationFooter
                page={pageData.page}
                perPage={pageData.per_page as 10 | 20 | 30}
                total={pageData.total}
                totalPages={pageData.total_pages}
                onPageChange={setPage}
                onPerPageChange={(value) => { setPerPage(value); setPage(1); }}
                label="resultados"
              />
            </>
          )}
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Detalhes da sugestão</DialogTitle>
            <DialogDescription>Contexto analítico e informações técnicas da sugestão.</DialogDescription>
          </DialogHeader>
          {detailQ.isLoading ? (
            <div className="text-sm text-muted-foreground">Carregando detalhe...</div>
          ) : detailQ.isError || !detailQ.data ? (
            <div className="text-sm text-destructive">{(detailQ.error as any)?.message || "Erro ao carregar detalhe."}</div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Campanha</div><div className="mt-2 text-sm font-medium">{detailQ.data.campaign_name}</div></Card>
                <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Dimensão</div><div className="mt-2 text-sm font-medium">{detailQ.data.dimension_name || "-"}</div></Card>
                <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Corte</div><div className="mt-2 text-sm font-medium">{cutTypeLabel(detailQ.data.cut_type)} • {detailQ.data.cut_name}</div></Card>
                <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Inventário</div><div className="mt-2 text-sm font-medium">{detailQ.data.inventory_name || "-"}</div></Card>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <Card className="p-4"><div className="mb-2 font-medium">Descrição Técnica do Risco</div><div className="whitespace-pre-wrap text-sm text-muted-foreground">{detailQ.data.risk_description || "-"}</div></Card>
                <Card className="p-4"><div className="mb-2 font-medium">Possíveis danos e agravos</div><div className="whitespace-pre-wrap text-sm text-muted-foreground">{detailQ.data.possible_damage || "-"}</div></Card>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <Card className="p-4"><div className="mb-2 font-medium">Exemplos de situação</div><div className="whitespace-pre-wrap text-sm text-muted-foreground">{detailQ.data.example_situations || "-"}</div></Card>
                <Card className="p-4"><div className="mb-2 font-medium">Orientações de controle</div><div className="whitespace-pre-wrap text-sm text-muted-foreground">{detailQ.data.control_guidelines || "-"}</div></Card>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <Card className="p-4"><div className="mb-2 font-medium">Justificativa da aprovação ou rejeição</div><div className="whitespace-pre-wrap text-sm text-muted-foreground">{infoAfterDecision(detailQ.data.status, detailQ.data.justification_text)}</div></Card>
                <Card className="p-4"><div className="mb-2 font-medium">Observação técnica da aprovação ou rejeição</div><div className="whitespace-pre-wrap text-sm text-muted-foreground">{infoAfterDecision(detailQ.data.status, detailQ.data.technical_notes_text)}</div></Card>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Histórico da sugestão</DialogTitle>
            <DialogDescription>Rastreamento simples das ações realizadas sobre a sugestão.</DialogDescription>
          </DialogHeader>
          {logsQ.isLoading ? (
            <div className="text-sm text-muted-foreground">Carregando histórico...</div>
          ) : logsQ.isError ? (
            <div className="text-sm text-destructive">{(logsQ.error as any)?.message || "Erro ao carregar histórico."}</div>
          ) : !(logsQ.data ?? []).length ? (
            <div className="text-sm text-muted-foreground">Nenhum log encontrado.</div>
          ) : (
            <div className="overflow-hidden rounded-xl border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Antes</TableHead>
                    <TableHead>Depois</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logsQ.data!.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{formatDate(log.created_at)}</TableCell>
                      <TableCell>{log.performed_by_name || "Sistema"}</TableCell>
                      <TableCell className="font-medium">{actionLabel(log.action)}</TableCell>
                      <TableCell className="max-w-[280px] whitespace-pre-wrap text-sm text-muted-foreground">{log.before_summary || "-"}</TableCell>
                      <TableCell className="max-w-[280px] whitespace-pre-wrap text-sm text-muted-foreground">{log.after_summary || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!actionMode} onOpenChange={(open) => !open && closeActionDialog()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {actionMode === "approve" ? "Aprovar sugestão" :
               actionMode === "reject" ? "Rejeitar sugestão" :
               actionMode === "reset" ? "Voltar sugestão para pendente" :
               actionMode === "convert" ? "Converter para inventário" :
               "Aprovar e converter para inventário"}
            </DialogTitle>
            <DialogDescription>
              Registre justificativa e observação técnica conforme necessário.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {(actionMode === "convert" || actionMode === "approve_and_convert") ? (
              <div>
                <div className="mb-2 text-sm font-medium">Inventário ativo *</div>
                <Select value={inventoryId} onValueChange={setInventoryId}>
                  <SelectTrigger><SelectValue placeholder="Selecione um inventário ativo" /></SelectTrigger>
                  <SelectContent>
                    {inventories.map((inventory) => (
                      <SelectItem key={inventory.id} value={inventory.id}>{inventory.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div>
              <div className="mb-2 text-sm font-medium">Justificativa {actionMode === "reject" ? "*" : "(opcional)"}</div>
              <Textarea value={justification} onChange={(e) => setJustification(e.target.value)} rows={4} />
            </div>
            <div>
              <div className="mb-2 text-sm font-medium">Observação técnica</div>
              <Textarea value={technicalNotes} onChange={(e) => setTechnicalNotes(e.target.value)} rows={5} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeActionDialog} disabled={mutationBusy}>Cancelar</Button>
            <Button onClick={confirmAction} disabled={mutationBusy}>Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
